// ============================================================================
// View management
// ============================================================================
import {
  onboarding, chatView, inner, modeBadge, sessionSub,
  stopBtn, sendBtn, input, attachBtn, typingIndicator,
  composerHint, queuedTray, messagesEl, scrollBottomBtn,
} from "./dom.js";

// Deps populated by initViewManager()
let closeSidebarRef = null;
let renderRecentDirsRef = null;
let clearActiveSessionRef = null;
let sendRef = null;
let getSessionActive = () => false;
let setSessionActiveRef = null;
let setBusyState = () => {};
let getQueueState = () => ({ running: null, queued: [] });
let getUserPinnedAway = () => false;
let setUserPinnedAway = () => {};
let getScrollTimer = () => null;
let setScrollTimer = () => {};
let getScrollBackFetching = () => false;
let setScrollBackFetching = () => {};
let getReplayingHistory = () => false;
let getReplayOldestCursor = () => null;
let getWs = () => null;
let trimDomMessagesRef = null;

export function showOnboarding() {
  onboarding.classList.remove("hidden");
  chatView.classList.add("hidden");
  closeSidebarRef();
  setSessionActiveRef(false);
  clearActiveSessionRef();
  renderRecentDirsRef();
}
export function showChatView() {
  onboarding.classList.add("hidden");
  chatView.classList.remove("hidden");
  closeSidebarRef();
  updateComposerCenter();
  input.focus();
}

// Center the composer in the empty state (new chat, no messages yet) and
// snap it back to the bottom once the first .msg-row appears. A
// MutationObserver keeps this in sync regardless of which code path
// populates #messages-inner (new session, cached history replay, etc.).
function updateComposerCenter() {
  chatView.classList.toggle("composer-centered", !inner.querySelector(".msg-row"));
}
export function setStatus(state, sub) {
  // The standalone traffic-light dot was removed; its status color now
  // reflects on the mode-badge border. We keep a `status-state` class on
  // the badge so CSS can color the border (green/yellow/red/muted).
  modeBadge.classList.remove("status-live", "status-busy", "status-err");
  if (state) modeBadge.classList.add("status-" + state);
  if (sub !== undefined) sessionSub.textContent = sub;
}
export function setBusy(b) {
  setBusyState(b);
  stopBtn.disabled = !b;
  // Input and send button stay enabled even when busy — the user can
  // queue additional messages or run slash commands at any time.
  const sessionActive = getSessionActive();
  sendBtn.disabled = !sessionActive;
  input.disabled = !sessionActive;
  attachBtn.disabled = !sessionActive;
  if (sessionActive) {
    if (b) {
      setStatus("busy", "working…");
      input.placeholder = "Devin is working… (type to queue)";
    } else {
      setStatus("live", "ready");
      input.placeholder = "Message Devin… (Enter to send, Shift+Enter for newline)";
      composerHint.textContent = "";
      // Don't clear queueState here — the server's queue_update is
      // authoritative. If the queue is genuinely empty the server will
      // send a queue_update with an empty queued array.
    }
    if (b) scrollDown();
  }
  // Always toggle the typing indicator, even when sessionActive is false.
  // Several handlers (session_stopped, disconnected) set sessionActive=false
  // before calling setBusy(false); if this were inside the guard above, the
  // three-dot animation would keep playing after the session ends.
  typingIndicator.classList.toggle("hidden", !b);

  // Toggle ACTIVE badge on the latest agent message row
  const activeBadges = document.querySelectorAll(".agent-active-badge");
  if (activeBadges.length > 0) {
    const lastBadge = activeBadges[activeBadges.length - 1];
    lastBadge.classList.toggle("hidden", !b);
  }
}

/** Render the queued-prompts tray above the composer, driven entirely by
 *  `queueState` (the authoritative snapshot from `queue_update` messages).
 *  Each chip shows its position, truncated text, an image count badge, a
 *  cancel (✕) button, and is draggable to reorder. The running prompt
 *  (if present) renders as a non-draggable chip with a spinner. */
export function renderQueuedTray() {
  if (!queuedTray) return;
  const queueState = getQueueState();
  const hasRunning = !!queueState.running;
  const queuedCount = queueState.queued.length;
  // Only show the tray when there are actually queued prompts waiting.
  // A single running prompt with nothing queued is already conveyed by the
  // typing indicator + "working…" status; the tray would just add noise.
  if (queuedCount === 0) {
    queuedTray.hidden = true;
    queuedTray.innerHTML = "";
    return;
  }
  queuedTray.hidden = false;
  let html = `<div class="queued-label"><span class="dot-mini"></span>Queued — next up</div>`;
  // Running chip (if any) — shown first, not draggable, ✕ cancels the turn.
  if (hasRunning) {
    const r = queueState.running;
    const imgs = r.images ? ` <span class="queued-imgs">+${r.images} img${r.images > 1 ? "s" : ""}</span>` : "";
    html += `<div class="queued-chip queued-chip-running" data-id="${escapeAttr(r.id)}"><span class="queued-spinner"></span><span class="queued-text"></span>${imgs}<button class="queued-cancel" title="Cancel running turn" data-cancel-id="${escapeAttr(r.id)}">✕</button></div>`;
  }
  // Queued chips — draggable, ✕ removes from queue.
  for (let i = 0; i < queuedCount; i++) {
    const q = queueState.queued[i];
    const imgs = q.images ? ` <span class="queued-imgs">+${q.images} img${q.images > 1 ? "s" : ""}</span>` : "";
    html += `<div class="queued-chip" draggable="true" data-id="${escapeAttr(q.id)}"><span class="queued-pos">${i + 1}</span><span class="queued-text"></span>${imgs}<button class="queued-cancel" title="Cancel this prompt" data-cancel-id="${escapeAttr(q.id)}">✕</button></div>`;
  }
  queuedTray.innerHTML = html;
  // Set text via textContent to avoid HTML injection from prompt text.
  // Turbo check prompts get a fixed label — their raw text is the sentinel
  // instructions, not user content.
  const chips = queuedTray.querySelectorAll(".queued-chip .queued-text");
  let chipIdx = 0;
  if (hasRunning) {
    chips[chipIdx].textContent = queueState.running.source === "turbo"
      ? "⚡ turbo check"
      : queueState.running.text || "(running)";
    chipIdx++;
  }
  for (let i = 0; i < queuedCount; i++) {
    const q = queueState.queued[i];
    chips[chipIdx + i].textContent = q.source === "turbo" ? "⚡ turbo check" : q.text || "(image)";
  }
  // Wire cancel buttons.
  const cancelBtns = queuedTray.querySelectorAll(".queued-cancel");
  for (const btn of cancelBtns) {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const id = btn.getAttribute("data-cancel-id");
      if (id) sendQueueCancel(id);
    });
  }
  // Wire drag-to-reorder on queued (non-running) chips.
  wireQueueDragDrop();
}

/** Escape a string for use in an HTML attribute value. */
function escapeAttr(s) {
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Send a queue_cancel message to the server. */
function sendQueueCancel(id) {
  const ws = getWs();
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify({ type: "queue_cancel", id }));
}

/** Send a queue_move message (move `id` to immediately after `afterId`,
 *  or to the front if afterId is null). */
function sendQueueMove(id, afterId) {
  const ws = getWs();
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify({ type: "queue_move", id, afterId }));
}

/** Wire native HTML drag-and-drop on queued chips for reordering.
 *  Running chips are not draggable. On drop, sends a `queue_move`
 *  message so the server applies the reorder authoritatively. */
function wireQueueDragDrop() {
  const chips = queuedTray.querySelectorAll(".queued-chip[draggable='true']");
  let dragSourceId = null;
  for (const chip of chips) {
    chip.addEventListener("dragstart", (e) => {
      dragSourceId = chip.getAttribute("data-id");
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", dragSourceId);
      chip.classList.add("queued-dragging");
    });
    chip.addEventListener("dragend", () => {
      dragSourceId = null;
      chip.classList.remove("queued-dragging");
    });
    chip.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      chip.classList.add("queued-drag-over");
    });
    chip.addEventListener("dragleave", () => {
      chip.classList.remove("queued-drag-over");
    });
    chip.addEventListener("drop", (e) => {
      e.preventDefault();
      chip.classList.remove("queued-drag-over");
      const sourceId = dragSourceId || e.dataTransfer.getData("text/plain");
      const targetId = chip.getAttribute("data-id");
      if (!sourceId || !targetId || sourceId === targetId) return;
      // Determine whether the drop is before or after the target chip
      // based on the cursor position within the chip.
      const rect = chip.getBoundingClientRect();
      const after = (e.clientX - rect.left) > rect.width / 2;
      // To move `sourceId` to immediately after `targetId`, we send
      // afterId = targetId. To move it before targetId, we send
      // afterId = the chip preceding targetId (or null if target is
      // already first).
      if (after) {
        sendQueueMove(sourceId, targetId);
      } else {
        // Find the queued chip visually before the target.
        const allChips = Array.from(queuedTray.querySelectorAll(".queued-chip[draggable='true']"));
        const targetIdx = allChips.indexOf(chip);
        if (targetIdx <= 0) {
          sendQueueMove(sourceId, null); // move to front
        } else {
          const prevId = allChips[targetIdx - 1].getAttribute("data-id");
          sendQueueMove(sourceId, prevId);
        }
      }
    });
  }
}
export function setTyping(show) {
  typingIndicator.classList.toggle("hidden", !show);
  if (show) scrollDown();
}
export function scrollDown(instant) {
  // `instant` forces a jump to the bottom, bypassing the userPinnedAway
  // guard and using auto (non-animated) behavior. This is used during
  // initial history load so the user lands at the newest message before
  // older pages are prepended (which would otherwise preserve a mid-
  // conversation scroll position and leave the user stuck in the middle).
  if (!instant && getUserPinnedAway()) return;
  if (instant) setUserPinnedAway(false);
  messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: instant ? "auto" : "smooth" });
}

/**
 * Initialize the view manager.
 * @param {object} deps
 */
export function initViewManager(deps) {
  closeSidebarRef = deps.closeSidebar;
  renderRecentDirsRef = deps.renderRecentDirs;
  clearActiveSessionRef = deps.clearActiveSession;
  sendRef = deps.send;
  getSessionActive = deps.getSessionActive;
  setSessionActiveRef = deps.setSessionActive;
  setBusyState = deps.setBusyState;
  getQueueState = deps.getQueueState;
  getUserPinnedAway = deps.getUserPinnedAway;
  setUserPinnedAway = deps.setUserPinnedAway;
  getScrollTimer = deps.getScrollTimer;
  setScrollTimer = deps.setScrollTimer;
  getScrollBackFetching = deps.getScrollBackFetching;
  setScrollBackFetching = deps.setScrollBackFetching;
  getReplayingHistory = deps.getReplayingHistory;
  getReplayOldestCursor = deps.getReplayOldestCursor;
  getWs = deps.getWs;
  trimDomMessagesRef = deps.trimDomMessages;

  new MutationObserver(updateComposerCenter).observe(inner, { childList: true, subtree: true });

  // Sticky-bottom semantics: the chat only auto-follows new content while
  // the user is essentially at the absolute bottom (AT_BOTTOM_EPS — tight
  // on purpose; the no-scroll margin is wide). Scrolling even slightly up
  // un-pins so incoming messages never yank the view. A "Scroll to bottom"
  // chip appears while un-pinned; clicking it re-pins.
  const AT_BOTTOM_EPS = 8;
  scrollBottomBtn.addEventListener("click", () => {
    haptic("tap");
    setUserPinnedAway(false);
    scrollBottomBtn.hidden = true;
    messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: "smooth" });
  });
  messagesEl.addEventListener("scroll", () => {
    const distFromBottom = messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight;
    const atBottom = distFromBottom <= AT_BOTTOM_EPS;
    setUserPinnedAway(!atBottom);
    scrollBottomBtn.hidden = atBottom;
    // While pinned, smooth-scroll can land a px or two short — gently
    // catch up so the flag doesn't flap on fractional heights.
    if (atBottom && distFromBottom > 1.5) {
      const scrollTimer = getScrollTimer();
      if (scrollTimer) clearTimeout(scrollTimer);
      setScrollTimer(setTimeout(() => {
        if (!getUserPinnedAway()) {
          messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: "smooth" });
        }
      }, 150));
    }
    // Scroll-back: when the user reaches the top and we have a cursor for
    // older history, fetch the next older page from the server.
    if (messagesEl.scrollTop < 50 && getReplayOldestCursor() && !getReplayingHistory() && !getScrollBackFetching()) {
      setScrollBackFetching(true);
      sendRef({ type: "history_fetch", cursor: getReplayOldestCursor(), direction: "older" });
    }
    // Reclaim memory when the user scrolls back toward the bottom: evict
    // oldest pages from the top that are now far above the viewport. This
    // bounds the DOM after heavy scroll-up without waiting for a live
    // update. No-op when the DOM is already within the window.
    if (atBottom && !getScrollBackFetching()) trimDomMessagesRef();
  });
}
